import React, { useState } from "react";
import styled from "@emotion/styled";

import {
  Button,
  Box,
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogContentText,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
  Rating,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Star as StarIcon, Check } from "@mui/icons-material";
import { setFeedbackDialogState } from "../../../redux/slices/search";
import { addFeedback } from "../../../firebase/utils";
import { RootState } from "../../../redux/store";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { QueryResult } from "../../../types/app";

const RelevantButton = styled(MuiToggleButton)<{ length: number }>`
  height: ${(props) => 560 / props.length}px;
  width: ${(props) => 560 / props.length}px;
  max-height: 45px;
  max-width: 45px;
  padding: 2px;
`;

const ToggleButton = styled(MuiToggleButton)`
  width: 25px;
  height: 25px;
  margin-left: 10px;
`;

const ToggleButtonGroup = styled(MuiToggleButtonGroup)`
  margin-top: 5px;
`;

const DialogContent = styled(MuiDialogContent)`
  padding-bottom: 0px;
`;

const ContentTop = styled.div`
  display: flex;
  justify-content: space-between;
  height: 50px;
  align-items: center;
  padding-right: 5px;
`;

const CheckIcon = styled(Check)`
  height: 20px;
  width: 20px;
`;

const TextField = styled(MuiTextField)`
  margin-top: 25px;
`;

const RatingNumber = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DialogActions = styled(MuiDialogActions)<{ error?: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.error ? "space-between" : "flex-end")};
  padding-right: 18px;
`;

function Feedback() {
  const [value, setValue] = useState<number | null>(null);
  const [hover, setHover] = useState(-1);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(false);
  const [remark, setRemark] = useState("");
  const [intendedResult, setIntendedResult] = useState("");
  const [relevant, setRelevant] = useState<number[]>([]);
  const dispatch = useAppDispatch();
  const state = useAppSelector((state: RootState) => state.main);
  const searchState = useAppSelector((state: RootState) => state.search);
  const query = searchState.prevQueryResult;
  const dialogOpen = searchState.feedbackDialogOpen;

  const handleRelevant = (
    event: React.MouseEvent<HTMLElement>,
    newRelevant: number[]
  ) => {
    setRelevant(newRelevant);
  };

  const handleSubmit = () => {
    if (value !== null) {
      dispatch(setFeedbackDialogState());
      const prevQuery = searchState.searchQuery;
      addFeedback(
        selected,
        value!,
        relevant,
        remark,
        intendedResult,
        prevQuery,
        searchState.prevQueryResult
      );
      setValue(null);
      setHover(-1);
      setSelected(false);
      setRemark("");
      setRelevant([]);
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <Dialog open={dialogOpen} onClose={() => undefined}>
      <DialogTitle>Feedback</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please leave your feedback for the previous search results for us to
          optimize our search engine.
        </DialogContentText>
        <ContentTop>
          <Box display="flex">
            <Typography marginTop="2px">
              Did you have an intended answer in mind?{" "}
            </Typography>
            <ToggleButton
              value="check"
              color="primary"
              selected={selected}
              onChange={() => {
                setSelected(!selected);
              }}
            >
              <CheckIcon />
            </ToggleButton>
          </Box>
          <Box display="flex">
            <Typography>Relevance Rating: </Typography>
            <Rating
              name="hover-feedback"
              value={value}
              precision={0.5}
              sx={{ marginLeft: "10px" }}
              onChange={(event, newValue) => {
                setValue(newValue);
              }}
              onChangeActive={(event, newHover) => {
                setHover(newHover);
              }}
              emptyIcon={
                <StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />
              }
            />
          </Box>
        </ContentTop>
        <Typography>
          Please select the relevant search results (hover to see results):{" "}
        </Typography>
        <ToggleButtonGroup value={relevant} onChange={handleRelevant}>
          {query.map((item: QueryResult, idx: number) => {
            return (
              <RelevantButton
                key={idx}
                color="primary"
                value={idx + 1}
                length={query.length}
              >
                <Tooltip
                  title={
                    <Box>
                      <Box>
                        {item.pdfName} page: {item.page}
                      </Box>
                      <Box>{item.description}</Box>
                    </Box>
                  }
                >
                  <RatingNumber>{idx + 1}</RatingNumber>
                </Tooltip>
              </RelevantButton>
            );
          })}
        </ToggleButtonGroup>
        <TextField
          margin="dense"
          label="Intended result of your search (if any) and
          specific page number/section if possible"
          type="text"
          fullWidth
          onChange={(e) => setIntendedResult(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Remarks (if any)"
          type="text"
          fullWidth
          onChange={(e) => setRemark(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        {error && (
          <Typography color="error" sx={{ marginLeft: "140px" }}>
            Please fill in the relevant details before submitting!
          </Typography>
        )}
        <Button onClick={handleSubmit} color="primary">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Feedback;
